/* eslint-disable @typescript-eslint/no-explicit-any */
import { ETicketFilter, ETicketStatus } from '@constants';
import { ITicketsFilters } from '@sdk/contracts';
import { DatePicker, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { Wrapper } from './FiltersHandler.styles';

import {
  StudentSearchSelector,
  TagSelector,
  TeacherSelector,
  VisibilitySelector
} from '@/components/Form';
import { CourseSearchFilter } from '@/components/Form/CourseSearchFilter';
import { Icon } from '@/components/Icon';
import { useAllCoursesForSelectors, useMyTicketsList, useProfile } from '@/store';

const INPUT_WIDTH = 110;

export const FiltersHandler = () => {
  const { t } = useTranslation(['CommonTicketsTranslations', 'DefaultTranslations']);
  const {
    roleCheck: { isManager }
  } = useProfile();
  const { filter, setFilter } = useMyTicketsList(ETicketFilter.OPEN);
  const { courses } = useAllCoursesForSelectors();

  const { issuers, status, courses: filteredCourses, tag, assignee, visibility } = filter;

  const statusOptions = Object.values(ETicketStatus).map(s => ({
    label: t(`DefaultTranslations:${s}`),
    value: s
  }));

  const handleSetFilters = (key: keyof ITicketsFilters, value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      clearFilter(key);
    } else {
      setFilter({ ...filter, [key]: value });
    }
  };

  const clearFilter = (key: keyof ITicketsFilters) => {
    const updatedFilters = { ...filter };
    delete updatedFilters[key];
    setFilter({ ...updatedFilters });
  };

  return (
    <Wrapper>
      <Select
        mode="multiple"
        allowClear
        value={status}
        options={statusOptions}
        onChange={v => handleSetFilters('status', v)}
        placeholder={t('CommonTicketsTranslations:STATUS')}
        suffixIcon={<Icon i="CaretDown" />}
      />

      <StudentSearchSelector
        mode="multiple"
        value={issuers}
        onChange={u => handleSetFilters('issuers', u)}
        placeholder={t('CommonTicketsTranslations:STUDENT')}
        allowClear
        style={{ width: INPUT_WIDTH }}
      />

      <DatePicker.RangePicker
        onChange={values => {
          const [from, to] = values || [];
          const normalized =
            from && to
              ? [from.startOf('day').toISOString(), to.endOf('day').toISOString()]
              : undefined;
          handleSetFilters('date', normalized);
        }}
      />

      <CourseSearchFilter
        mode="multiple"
        placeholder={t('CommonTicketsTranslations:COURSE')}
        value={filteredCourses}
        onChange={c => handleSetFilters('courses', c)}
        courses={courses || []}
      />

      {isManager && (
        <>
          <TagSelector
            value={tag}
            maxTagCount="responsive"
            onChange={tagSelected => handleSetFilters('tag', tagSelected)}
            allowClear
          />
          <TeacherSelector
            value={assignee}
            onChange={teacher => handleSetFilters('assignee', teacher)}
            allowClear
            suffixIcon={<Icon i="CaretDown" />}
          />
          <VisibilitySelector
            value={visibility}
            onChange={v => handleSetFilters('visibility', v)}
            allowClear
            suffixIcon={<Icon i="CaretDown" />}
          />
        </>
      )}
    </Wrapper>
  );
};
